// Deterministic contract money policy (client side).
// Single source of truth for parsing contract amounts into integer cents.
// Mirrors server/services/contractAdjustmentDomain.ts::parseMoneyToCents —
// the two implementations must stay in sync (both are covered by unit tests
// over the same canonical forms).
//
// Accepted canonical forms (all → 2822825 cents, displayed "28.228,25 €"):
//   "28228,25"   es-ES decimal comma
//   "28228.25"   plain decimal point
//   "28.228,25"  es-ES thousands dot + decimal comma
// Rejected: malformed/ambiguous values and more than two decimals
// ("28228,255", "28.228.25", "1,234,567.89", "abc", "" …).
// Integer-cents arithmetic only — never floating-point conversion as source of truth.

export const MONEY_PREVIEW_PREFIX = "Importe que se guardará:";
export const MONEY_INVALID_ERROR =
  "Importe no válido. Usa un número con máximo dos decimales (ej. 28.228,25).";

export function parseMoneyToCents(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [euros, decimals = ""] = unsigned.split(".");
  const cents = Number(euros) * 100 + Number((decimals + "00").slice(0, 2));
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

// Canonical storage form sent to the server: "28228.25"
export function formatCentsAsDecimal(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}

// es-ES display form: "28.228,25" (no currency symbol, no Intl dependency)
export function formatCentsAsEs(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  const euros = String(Math.floor(absolute / 100));
  const grouped = euros.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${grouped},${String(absolute % 100).padStart(2, "0")}`;
}

// Pre-save preview label, e.g. "Importe que se guardará: 28.228,25 €"
export function moneyPreviewLabel(cents: number, currency = "EUR"): string {
  const symbol = currency === "EUR" ? "€" : currency;
  return `${MONEY_PREVIEW_PREFIX} ${formatCentsAsEs(cents)} ${symbol}`;
}

// Returns true when the raw input is a canonical/accepted money form that the
// server will accept unchanged; used to gate the submit button.
export function isValidMoneyInput(value: string | null | undefined): boolean {
  return parseMoneyToCents(value) !== null;
}

// Canonical value to persist (null clears the amount), or null when invalid.
export function canonicalMoneyOrNull(value: string | null | undefined): string | null {
  const cents = parseMoneyToCents(value);
  return cents === null ? null : formatCentsAsDecimal(cents);
}
